// Restoran qurilmalari (Q-021, Q-024): qurilma restoran logini bilan kiradi, ega yoki menejer uni o'sha
// qurilmaning o'zida PIN bilan tasdiqlaydi. Platforma bu yerda ko'radi, uzadi va qayta yoqadi.
import { useState } from 'react';
import { Alert, Badge, Button, Card, EmptyState, Modal, useAction } from '@onepos/ui';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { LIMIT_LABELS, deviceKindLabel, fmtAgo } from '../../lib/format';
import type { TenantDetail } from '../../lib/types';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function DevicesTab({ data, reload }: { data: TenantDetail; reload: () => void }) {
  const me = useMe();
  const canEdit = me.role !== 'billing';
  const [confirm, setConfirm] = useState<TenantDetail['terminals'][number] | null>(null);
  const branchName = (id: string) => data.branches.find((b) => b.id === id)?.name ?? '—';
  const toggle = useAction(async (id: string, active: boolean) => {
    await api.rpc('platform_terminal_set_active', { p_terminal: id, p_active: active });
    setConfirm(null);
    reload();
  });
  const limits = Object.entries(data.limits ?? {}).filter(([k, l]) => k !== 'printer' && (l.max > 0 || l.used > 0));
  const pending = data.terminals.filter((t) => t.is_active && !t.approved).length;

  return (
    <>
      <Card title="Qurilmalar">
        <p className="cn-muted" style={{ marginTop: 0 }}>
          {limits.length > 0
            ? limits.map(([k, l]) => `${cap(LIMIT_LABELS[k] ?? k)}: ${l.used} / ${l.max}`).join(' · ') + ' · ega qurilmalari limitsiz.'
            : 'Ega qurilmalari limitsiz.'}{' '}
          Yangi qurilma restoran logini bilan kiradi, ega yoki menejer uni shu qurilmada PIN bilan tasdiqlaydi.
        </p>
        {pending > 0 && <Alert tone="warning">{pending} ta qurilma restoran ichida tasdiq kutmoqda.</Alert>}
        {toggle.error && <Alert tone="danger">{toggle.error}</Alert>}
        {data.terminals.length === 0 ? (
          <EmptyState title="Hali qurilma yo‘q">Restoran logini va parolini egaga bering — u qurilmada kiradi va tasdiqlaydi.</EmptyState>
        ) : (
          <table className="cn-table">
            <thead>
              <tr>
                <th>Qurilma</th>
                {data.branches.length > 1 && <th>Filial</th>}
                <th>Holat</th>
                <th>Oxirgi faollik</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.terminals.map((t) => (
                <tr key={t.id}>
                  <td data-label="Qurilma">
                    <span className="cn-table__name">{t.name}</span>
                    <span className="cn-table__sub">
                      {t.approved ? deviceKindLabel(t.kind) : 'Turi tasdiqlashda tanlanadi'}
                      {t.app_version ? ` · v${t.app_version}` : ''}
                    </span>
                  </td>
                  {data.branches.length > 1 && <td data-label="Filial">{branchName(t.branch_id)}</td>}
                  <td data-label="Holat">
                    <Badge tone={!t.is_active ? 'neutral' : t.approved ? 'success' : 'warning'}>
                      {!t.is_active ? 'Uzilgan' : t.approved ? 'Ulangan' : 'Tasdiq kutmoqda'}
                    </Badge>
                  </td>
                  <td data-label="Oxirgi faollik">{fmtAgo(t.last_seen_at)}</td>
                  <td className="cn-num">
                    {canEdit &&
                      (t.is_active ? (
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(t)}>
                          Uzish
                        </Button>
                      ) : (
                        t.approved && (
                          <Button size="sm" variant="ghost" disabled={toggle.busy} onClick={() => void toggle.run(t.id, true)}>
                            Qayta yoqish
                          </Button>
                        )
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        busy={toggle.busy}
        title="Qurilmani uzish"
        footer={
          <>
            <Button onClick={() => setConfirm(null)} disabled={toggle.busy}>
              Bekor qilish
            </Button>
            <Button variant="danger" disabled={toggle.busy} onClick={() => confirm && void toggle.run(confirm.id, false)}>
              Uzish
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          <strong>{confirm?.name}</strong> darhol ishlamay qoladi, undagi xodim sessiyalari yopiladi. Keyin qayta yoqish mumkin.
        </p>
      </Modal>
    </>
  );
}
